import { Component, Input, OnInit, ViewChild, inject } from '@angular/core';
import { BaseComponent, WmsToggleComponent, WmsMessageComponent } from '@wms/app/components';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { JKS_FILE_PATH, SecurityService, getValidFileName, openFile, DatabaseComponent, KEY_STORE_OPTIONS } from '@wms/settings/security';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { isValidWebURL } from '@wm/core';
import { FileService } from '@wms/app/services';
import { includes, isEqual } from 'lodash-es';
import { Subscription } from 'rxjs';

@Component({
  standalone: true,
  selector: 'wms-saml',
  templateUrl: './saml.component.html',
  styleUrls: ['./saml.component.less'],
  imports: [CommonModule, FormsModule, WmsToggleComponent, WmsMessageComponent, DatabaseComponent, TooltipModule]
})
export class SAMLComponent extends BaseComponent implements OnInit {
  @Input() securityProps: any;
  @Input() authInfo: any;
  securityService: SecurityService;
  security: any;
  isMetadataLoaded: any;
  isDeploying: any;
  appRunUrl: any;
  isLoading: any;
  RegEx: any = {
    XML_FILE: /[\w\s-]+.xml/i,
    JKS_FILE: /.+.jks/i
  }
  JKS_FILE_PATH: any = JKS_FILE_PATH;
  @ViewChild('samlForm', { static: true }) ngForm: NgForm;
  formChangesSubscription: Subscription;
  resetFormSubscription: Subscription;

  constructor(private fileService: FileService) {
    super();
    this.securityService = inject(SecurityService);
  }

  ngOnInit(): void {
    let constructedUrl  = window.location.protocol + '//' + window.location.host;
    this.security = this.securityService.security;
    this.security.userSAMLRoleProviders = ['SAML', 'Database'];
    this.security.keyStoreValue         = KEY_STORE_OPTIONS[0];
    this.security.keyStoreOptions       = KEY_STORE_OPTIONS;
    this.security.signonUrl               = constructedUrl + '/studio/services/login/saml2/sso/saml';
    this.security.signoutUrl              = constructedUrl + '/studio/services/logout/saml2/slo';
    this.security.audienceUrl             = constructedUrl + '/studio/services/saml2/service-provider-metadata/saml';
    this.security.subjectName             = this.securityService.SUBJECT_NAME;
    if (this.security.enableSecurity) {
      //hiding error messages if any, since provider was changed
      this.securityService.hideMessage(true);
    }
    //show only user colmn in database configuration.
    this.security.showUnamePass = false;
    this.security.idpMetadataSource       = this.security.metadataSrcOptionsMap.URL;
    this.securityService.getSAMLOptions(this.authInfo);
    let setInitialValue: boolean = true;
    if (this.securityService.samlFormInitialValue) {
      setInitialValue = false;
      setTimeout(() => {
        this.ngForm.form.patchValue(this.securityService.samlFormInitialValue);
      });
    }

    this.formChangesSubscription = this.ngForm.form.valueChanges.subscribe(val => {
      if (!this.ngForm.form.dirty && setInitialValue) {
        this.securityService.samlFormInitialValue = this.ngForm.form.value;
      }
      if (this.ngForm.form.dirty) {
        if (isEqual(this.securityService.samlFormInitialValue, this.ngForm.form.value)) {
          this.security.dirtyCheckList.splice(this.security.dirtyCheckList.indexOf('samplProvider', 1));
        } else if (!includes(this.security.dirtyCheckList, 'samplProvider')) {
          this.security.dirtyCheckList.push('samplProvider');
        }
      }
    });


    this.resetFormSubscription = this.securityService.resetForm.subscribe((resetInitialVal) => {
      if (!resetInitialVal) {
        this.ngForm.form.reset(this.securityService.samlFormInitialValue);
      } else {
        this.securityService.samlFormInitialValue = this.ngForm.form.value;
      }
    });
    // deploy the application to populate metadata url.
    this.appRunUrl = this.projectService.getAppDeployedUrl();
    if (!this.appRunUrl || this.appRunUrl === '') {
      this.isDeploying = true;
      this.projectService.getAppRunURL(false).then(() => {
        this.appRunUrl = this.projectService.getAppDeployedUrl();
        this.isDeploying = false;
        this.populateUrls();
      }).catch(() => {
        this.isDeploying = false;
        this.populateUrls();
        this.securityService.hideMessage(false)
        this.securityService.showMessage(this.locale.MESSAGE_SAML_DEPLOY_FAILED,'error')
      });
    }
    this.isMetadataLoaded = this.security.idpPublicKey ? true : false;
    this.populateUrls();
    // this.handleWatchers(provider);
  }

  getFormData(inputEleName: any, regEx: any, noSelectedFileMessage: any) {
    let elementId: any = inputEleName === 'jks-file' ? 'wm-security-saml-jks-file' : 'wm-security-saml-metadata-file'
    let form = (document.getElementById(elementId) as any)?.files,
      fileName = form[0].name,
      formData;
    this.securityService.hideMessage(true)
    if (!form.length) {
      this.securityProps.message = {
        caption: this.locale[noSelectedFileMessage],
        type: 'info'
      };
      return false;
    }
    // file name should not have spl characters except '-'.
    if (fileName.search(regEx)) {
      this.securityService.hideMessage(false)
      this.securityService.showMessage(this.locale.MESSAGE_SAML_NO_SPL_CHARACTERS_FILE_NAME,'error')
      return false;
    }
    formData = new FormData();
    formData.append('file', form[0]);
    return formData;
  }

  uploadMetadataFile() {
    let formData,
      uploadConfig;

    formData = this.getFormData('metadata-file', this.RegEx.XML_FILE, 'MESSAGE_ERROR_NO_METADATA_FILE_SELECTED_DESC');
    if (!formData) {
      return;
    }
    this.isLoading = true;
    uploadConfig = {
      'content': formData
    };
    (this.securityService.uploadIdpMetadata(uploadConfig) as Promise<any>).then((response) => {
      this.isLoading = false;
      this.isMetadataLoaded = true;
      this.security.idpPublicKey = response.idpSignatureKey;
      this.security.idpEndpointUrl = response.singleSignOnServices[0];
      this.security.metadataFileName = this.securityService.METADATA_FILE_NAME;
    }).catch(() => {
      this.isLoading = false;
      this.securityService.hideMessage(false)
      this.securityService.showMessage(this.locale.MESSAGE_SAML_METADATA_VALIDATION_FAILED,'error')
    });
  }

  setMetaUrlDirty() {
    this.securityService.isUrlDirty = true;
  }

  execOpenFile(path: any, fileName: any) {
    openFile(path, fileName);
  }

  copyToClipboard($event: any) {
    let $temp = $($event.target).closest('.input-group').find('input');
    $temp.select();
    document.execCommand('copy');
  }

  // populating fields based on runtime url.
  populateUrls() {

    if (this.isDeploying) {
      this.security.spMetadataUrl = this.locale.ICONTITLE_LOADING_URL;
    } else if (this.appRunUrl) {
      this.security.spMetadataUrl = window.location.protocol + this.appRunUrl + '/saml2/service-provider-metadata/saml';
    } else {
      this.security.spMetadataUrl = this.locale.MESSAGE_SAML_SP_METADATA_DEPLOY_FAILED;
    }
  }

  uploadJksFile() {
    let formData: any,
      uploadConfig;
    formData = this.getFormData('jks-file', this.RegEx.JKS_FILE, 'MESSAGE_ERROR_NO_JKS_FILE_SELECTED_DESC');
    if (!formData) {
      return;
    }
    uploadConfig = {
      'path': JKS_FILE_PATH,
      'action': 'upload',
      'content': formData
    };
    this.fileService.upload(uploadConfig).then(() => {
      this.security.keyStoreName = getValidFileName(formData.get('file').name);
      this.security.keyStoreLocation = JKS_FILE_PATH;
    });
  }

  // called when keystore is removed.
  resetKeystoreOptions() {
    this.security.keyStoreName = null;
    this.security.createKeystore = null;
  }

  // Loads the idp url and populates websso url.
  validateIdpUrl() {
    this.securityService.hideMessage(true)
    if (!this.security.idpMetadataUrl) {
      this.securityService.hideMessage(false)
      this.securityService.showMessage(this.locale.MESSAGE_SAML_IDP_METADATA_NOT_SET,'error')
      return;
    } else if (!isValidWebURL(this.security.idpMetadataUrl)) {
      this.securityService.hideMessage(false)
      this.securityService.showMessage(this.locale.MESSAGE_SAML_IDP_METADATA_INVALID,'error')
      return;
    }

    this.isLoading = true;
    // for on load reset we send true as parameter.
    this.securityService.resetSAMLConfig(true);
    this.isMetadataLoaded = false;
    (this.securityService.loadIdpMetadata({
      idpMetadataUrl: this.security.idpMetadataUrl
    }) as Promise<any>).then((response) => {
      this.securityService.isUrlDirty = false;
      this.isLoading = false;
      this.isMetadataLoaded = true;
      this.security.idpPublicKey = response.idpSignatureKey;
      this.security.idpEndpointUrl = response.singleSignOnServices[0];
    }).catch(() => {
      this.isLoading = false;
      this.securityService.hideMessage(false);
      this.securityService.showMessage(this.locale.MESSAGE_SAML_METADATA_VALIDATION_FAILED,'error')
    });
  }

  override ngOnDestroy(): void {
    this.formChangesSubscription?.unsubscribe();
    this.resetFormSubscription?.unsubscribe();
  }
}
